import { useCallback, useEffect, useMemo, useRef } from "react";
import { apiFetch } from "../api/apiFetch";

export function getInterventionSecondaryLabel(intervention) {
  if (!intervention?.primary_action) return "";
  if (intervention.state === "reentry" && intervention.mode === "solo") {
    return "Do one small habit";
  }
  if (intervention.state === "active" && intervention.mode === "sprint") {
    return "Open Accountability";
  }
  return "";
}

export function useInterventionCard({
  intervention,
  onShown,
  surface = "in_app",
}) {
  const shownRef = useRef("");

  const postInterventionEvent = useCallback(async (eventType, metadata = {}) => {
    try {
      await apiFetch("/intervention/events/", {
        method: "POST",
        body: JSON.stringify({
          event_type: eventType,
          metadata,
        }),
      });
    } catch {
      // ignore analytics failures
    }
  }, []);

  useEffect(() => {
    const nudgeKey = intervention?.dominant_nudge?.key;
    if (!nudgeKey || shownRef.current === nudgeKey) return;
    shownRef.current = nudgeKey;
    postInterventionEvent("nudge_shown", {
      nudge_key: nudgeKey,
      state: intervention?.state,
      mode: intervention?.mode,
      target_action: intervention?.primary_action?.key || "",
      channel: "in_app",
      surface,
    });
    onShown?.(nudgeKey);
  }, [intervention, onShown, postInterventionEvent, surface]);

  const secondaryLabel = useMemo(
    () => getInterventionSecondaryLabel(intervention),
    [intervention]
  );

  const beginPrimaryAction = useCallback(async () => {
    const actionKey = intervention?.primary_action?.key;
    if (!actionKey) return "";
    await postInterventionEvent("nudge_opened", {
      nudge_key: intervention?.dominant_nudge?.key || "",
      state: intervention?.state,
      mode: intervention?.mode,
      target_action: actionKey,
      channel: "in_app",
      surface,
    });
    await postInterventionEvent("restart_action_started", {
      action_key: actionKey,
      source_nudge_key: intervention?.dominant_nudge?.key || "",
      state_before: intervention?.state,
      mode: intervention?.mode,
      channel: "in_app",
      surface,
    });
    return actionKey;
  }, [intervention, postInterventionEvent, surface]);

  const completePrimaryAction = useCallback(async (actionKey) => {
    if (!actionKey) return;
    await postInterventionEvent("restart_action_completed", {
      action_key: actionKey,
      source_nudge_key: intervention?.dominant_nudge?.key || "",
      state_before: intervention?.state,
      mode: intervention?.mode,
      channel: "in_app",
      surface,
    });
  }, [intervention, postInterventionEvent, surface]);

  return {
    secondaryLabel,
    postInterventionEvent,
    beginPrimaryAction,
    completePrimaryAction,
  };
}
